import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import ErrorBoundary from './components/ErrorBoundary';
import DevLogin from './pages/DevLogin';
import HubPicker from './pages/HubPicker';
import WelcomeGreeting from './components/WelcomeGreeting';
import IdleLogout from './components/IdleLogout';
import { restoreScroll } from './lib/updateGuard';
import './index.css';

// Route-level code splits — each chunk is loaded on demand so the
// initial mobile bundle stays small. Detailer + Booking were
// previously direct-imported, dragged into the main bundle for every
// owner/principal session even though only Dashboard mounts first.
//   - Dashboard: owner/principal land on HubPicker first, then click a
//     tile to /dashboard. Static-importing it dragged the 6.9k-line
//     page + the whole Supabase API layer (apiSupabase) + every tab
//     component into main.js for EVERY session (detailers included,
//     who never open it). Lazy + idle-prefetch (below) keeps main.js
//     lean while the chunk is warm before the user clicks through.
//   - Detailer: detailer-only route; owner/principal never hit it.
//   - Booking:  owner/principal route, but rare (clicked from
//     Dashboard's + Book Job button). Worth the ~1-frame fetch on
//     the click to keep mobile cold-load lean.
//   - Books:    3CAM, owner-only.
//   - Marketing: 3cMA, owner-only.
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Dashboard'));
const Detailer = lazy(() => import(/* webpackChunkName: "detailer" */ './pages/Detailer'));
const Booking  = lazy(() => import(/* webpackChunkName: "booking"  */ './pages/Booking'));
const Books    = lazy(() => import(/* webpackChunkName: "books"    */ './pages/Books'));
const Marketing= lazy(() => import(/* webpackChunkName: "marketing"*/ './pages/Marketing'));
const DealerApp= lazy(() => import(/* webpackChunkName: "dealer"   */ './pages/DealerApp'));

// In-app PIN login: a 4-digit PIN signs into Supabase and auto-routes to the
// matching role's profile. (Replaced the old bounce to /3c-hub.html.)
function LoginGate() {
  return <DevLogin />;
}

function ProtectedRoute({ children, allowedRoles }) {
  const { authed, role } = useAuth();
  if (!authed) return <LoginGate />;
  // Signed in, but this route isn't theirs → send them to '/' so the root
  // router picks their own home.
  //
  // This used to render <LoginGate/> in place, which stranded people: DevLogin
  // does NOT navigate after a successful PIN (it assumes you're at '/'), so
  // signing out of a role-gated route and back in as a DIFFERENT role left you
  // at that same URL, failing this check again and re-rendering the PIN pad.
  // The login looked broken while actually having succeeded — and it only
  // happened for some roles, which is what made it read as flaky.
  if (allowedRoles && !allowedRoles.includes(role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { authed, role, sites } = useAuth();
  // Prefetch the route chunks the user is most likely to navigate to
  // next, on idle. Saves the on-click chunk fetch (~50-200ms on mobile)
  // without bloating the initial bundle.
  useEffect(() => {
    if (!authed) return;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const handle = idle(() => {
      if (role === 'owner' || role === 'principal' || role === 'hd') {
        // Dashboard first — it's the tile both roles tap from the hub,
        // so warm its chunk before the slower-path Booking prefetch.
        import(/* webpackChunkName: "dashboard" */ './pages/Dashboard').catch(() => {});
        import(/* webpackChunkName: "booking" */ './pages/Booking').catch(() => {});
      }
      if (role === 'owner') {
        // 3CAM / 3cMA tiles in the hub launch new pages — prefetch
        // chunks so first click is instant. Detailer chunk never
        // prefetched here (irrelevant to owner/principal).
        import(/* webpackChunkName: "books"    */ './pages/Books').catch(() => {});
        import(/* webpackChunkName: "marketing"*/ './pages/Marketing').catch(() => {});
      }
    });
    return () => { try { if (window.cancelIdleCallback) window.cancelIdleCallback(handle); else clearTimeout(handle); } catch (_) {} };
  }, [authed, role]);
  return (
    <>
    <IdleLogout />
    <WelcomeGreeting />
    <Routes>
      {/* After login, land on the program picker so the user chooses which
          program to open (owner/principal). Detailer has one app → straight in. */}
      {/* An account that can reach more than one shop ALWAYS lands on the hub —
          for it, the hub IS the location picker, so redirecting past it removes
          the only way to switch. Ric exposed this: at Kingston his role resolves
          to `detailer`, so the rule below bounced him straight back to /detailer
          and his ⌂ Hub button looked like it merely refreshed the page.
          Single-location accounts still go straight to their one app. */}
      <Route path="/" element={
        !authed ? <LoginGate />
        : (sites && sites.length > 1) ? <HubPicker />
        : role === 'detailer' ? <Navigate to="/detailer" replace />
        : role === 'automotive-supervisor' ? <Navigate to="/detailer" replace />
        : role === 'tax-advisor' ? <Navigate to="/books" replace />
        : role === 'otto' ? <Navigate to="/dealer" replace />
        : <HubPicker />
      } />
      {/* `hd` (Ric at Albany) runs the same shop-ops shell as owner/principal.
          His surface is narrowed by HD_KEYS in viewMode.js, not by a separate
          page — the whole point of the multi-site build is one codebase. */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['owner', 'principal', 'hd']}>
          <Suspense fallback={<div style={{padding:40,color:'var(--muted)',fontFamily:'Inter,sans-serif',fontSize:13}}>Loading…</div>}>
            <Dashboard />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/booking" element={
        <ProtectedRoute allowedRoles={['owner', 'principal', 'hd']}>
          <Suspense fallback={<div style={{padding:40,color:'var(--muted)',fontFamily:'Inter,sans-serif',fontSize:13}}>Loading…</div>}>
            <Booking />
          </Suspense>
        </ProtectedRoute>
      } />
      {/* 3COT -- the dealer portal. A scoped role, not a separate program:
          its three surfaces live in DealerApp and everything it can reach is
          fenced by RLS off the JWT dealer claim (migration 20260810230000). */}
      <Route path="/dealer" element={
        <ProtectedRoute allowedRoles={['otto']}>
          <Suspense fallback={<div style={{padding:40,color:'var(--muted)',fontFamily:'Inter,sans-serif',fontSize:13}}>Loading 3COT…</div>}>
            <DealerApp />
          </Suspense>
        </ProtectedRoute>
      } />
      {/* HD gets the same full shop shell at BOTH shops (Ryder, 2026-10-26:
          "both of his profiles kingston and albany should be identical"). The
          detailer shell has no location pill, so landing him here at Kingston
          also stranded him there. */}
      <Route path="/detailer" element={
        role === 'hd' ? <Navigate to="/dashboard" replace /> :
        <ProtectedRoute>
          <Suspense fallback={<div style={{padding:40,color:'var(--muted)',fontFamily:'Inter,sans-serif',fontSize:13}}>Loading…</div>}>
            <Detailer />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/books" element={
        <ProtectedRoute allowedRoles={['owner', 'tax-advisor']}>
          <Suspense fallback={<div style={{padding:40,color:'var(--muted)',fontFamily:'Inter,sans-serif',fontSize:13}}>Loading 3CAM…</div>}>
            <Books />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/marketing" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <Suspense fallback={<div style={{padding:40,color:'var(--muted)',fontFamily:'Inter,sans-serif',fontSize:13}}>Loading 3cMA…</div>}>
            <Marketing />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/checklist" element={<Navigate to="/detailer" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  // After a seamless auto-update reload, put the user back where they were.
  // Tab / date / expanded-card already restore via their own sessionStorage
  // keys; this restores scroll offset in the lazily-mounted scroll containers.
  // (Importing updateGuard also installs the window.__updateGuards /
  // __captureRestoreState bridge the public/index.html controller reads.)
  useEffect(() => { restoreScroll(); }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}
